import { Component, Element, State, Prop, h, Event, EventEmitter, Method, Listen, Host } from '@stencil/core';
import { computePosition, autoUpdate, offset as fuiOffset, flip, size, Placement } from '@floating-ui/dom'
import { DropdownDirection, DropdownSize } from './types';

@Component({
  tag: 'zpr-dropdown',
  styleUrl: 'dropdown.scss'
})
export class ZprDropdown {
  private _placement: Placement = 'bottom-start'
  private _mainEl: HTMLElement
  private _footerEl: HTMLElement
  private _cleanupUpdate: () => void

  @Element() el: HTMLElement;

  @Prop({ reflect: true, mutable: true }) direction: DropdownDirection = DropdownDirection.Down;
  @Prop({ reflect: true }) size = DropdownSize.Default;
  @Prop() anchor: HTMLElement | undefined;
  @Prop() zIndex: string | undefined;
  @Prop() offset = { left: 0, top: 0 };
  @Prop() maxWidth: number;
  @Prop() minWidth: number = 128;
  @Prop() minHeight: number = 40; // 1 item
  @Prop() isFixedPosition: boolean = false;

  @State() isOpen: boolean = false;

  @Event({ eventName: 'dropdown-open', composed: true, cancelable: true, bubbles: true }) openStatus: EventEmitter<boolean>

  componentWillLoad() {
    if (typeof this.anchor === 'undefined') {
      this.anchor = this.el.parentElement;
    }
  }

  componentDidLoad() {
    // Increase z-index if inside a zpr-modal
    const modal = this.el.closest('zpr-modal')
    if (modal) this.el.style.zIndex = '999';
    if (this.zIndex) this.el.style.zIndex = this.zIndex;
    document.body.append(this.el)
    if (this.direction === DropdownDirection.Right) {
      this._placement = 'right-start';
    } else if (this.direction === DropdownDirection.RightUp) {
      this._placement = 'right-end';
    } else if (this.direction === DropdownDirection.Left) {
      this._placement = 'bottom-end';
    }
  }

  @Method()
  setPlacement(placement: Placement) {
    this._placement = placement
    this.updatePosition()
  }

  @Method()
  async open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.updatePosition()
    this._cleanupUpdate = autoUpdate(this.anchor, this.el, this.updatePosition.bind(this))
    this.openStatus.emit(true);
  }

  @Method()
  async close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._cleanupUpdate()
    this.openStatus.emit(false);
  }

  @Method()
  async isOpened() {
    return this.isOpen
  }

  @Method()
  async toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  setSize({ availableWidth, availableHeight, elements }) {
    const styles = {
      maxWidth: this.maxWidth ? `${this.maxWidth}px` : `${Math.max(this.minWidth, availableWidth - 16)}px`,
      maxHeight: `${Math.max(this.minHeight, availableHeight - 16)}px`,
    }
    Object.assign(elements.floating.style, styles)
  }

  updatePosition() {
    if (this._mainEl && this._footerEl) {
      this._mainEl.style.maxHeight = 'none'
    }

    computePosition(this.anchor, this.el, {
      placement: this._placement,
      middleware: [fuiOffset(8), flip(), size({ apply: this.setSize.bind(this) })]
    }).then(({ x, y }) => {
      const leftPos = this.isFixedPosition ? this.offset.left : x + this.offset.left
      const topPos = y + this.offset.top
      Object.assign(this.el.style, { left: `${leftPos}px`, top: `${topPos}px` })
      if (this._mainEl && this._footerEl) {
        const overallRect = this.el.getBoundingClientRect()
        const footerRect = this._footerEl.getBoundingClientRect()
        const height = Math.max(this.minHeight, overallRect.height - footerRect.height)
        this._mainEl.style.maxHeight = `${height}px`
      }
    })
  }

  @Listen('click', { target: 'window' })
  onBodyClick(ev: MouseEvent) {
    if (this.anchor.contains(ev.target as any) || this.el.contains(ev.target as any)) return
    this.close()
  }

  render() {
    return <Host class={{ open: this.isOpen }}></Host>
  }

  componentDidRender() {
    this._mainEl = this.el.querySelector(':scope > [slot="body"] > main')
    this._footerEl = this.el.querySelector(':scope > [slot="body"] > footer')
  }
}
